import Axios, { AxiosRequestHeaders } from 'axios';
import { Route } from 'vue-router';

import { CollectionType } from '@/enums/collection-type';
import { TitleOrder } from '@/enums/title-order';

import { cancellableRequest, getAuthHeaders, getAxios, getAxiosCached } from '@/helpers/axios-helper';
import { captureMessageForSentry, Sentry } from '@/helpers/sentry-helper';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { TabType } from '@/interfaces/inner-tabs';
import { PageOptions } from '@/interfaces/page-options';
import { NewDayResponse, NewProviderResponse, NewResponse } from '@/interfaces/responses/new';
import { NewestEpisodesResponse } from '@/interfaces/responses/newest-episodes';
import { PopularPersonResponse } from '@/interfaces/responses/person';
import { PopularResponse } from '@/interfaces/responses/popular';
import { SingleProviderResponse } from '@/interfaces/responses/single-provider';
import { TitleListFull, TitleListFullResponse, TitleListName } from '@/interfaces/responses/title-list';
import {
	NewTitle,
	NewWatchlistTitle,
	OfferMonetizationType,
	OfferMonetizationTypes,
	PopularTitle,
	SeenMutation,
	Title,
	TitleObjectType,
	WatchlistTitleResponse,
} from '@/interfaces/titles';

import { ObjectType } from '@/@types/graphql-types';
import { extendRuntimeFilterRange } from '@/helpers/filter-helper';
import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { getVm } from '@/helpers/vm-helper';
import { FilterCollection } from '@/stores/filter.store';
import { TitleStoreStateList } from '@/stores/title.store';

import { TitleDetailNodeSeason, TitleDetailsNode } from '@/interfaces/title-details-graphql';

interface UpcomingParams {
	nationwide_cinema_releases_only?: boolean;
	body?: string;
}

const baseFields = [
	'full_path',
	'full_paths',
	'id',
	'localized_release_date',
	'object_type',
	'poster',
	'scoring',
	'title',
	'tmdb_popularity',
	'backdrops',
	'production_countries',
];
export interface TitleEventPayload {
	title: PopularTitle | NewTitle;
	index?: number;
	date?: string;
	provider?: NewProviderResponse;
}

export interface TitleEventPayloadGraphql {
	title: {
		objectType: ObjectType;
		objectId: number;
		content?: {
			seasonNumber: number;
		};
		show?: {
			objectType: ObjectType;
			objectId: number;
		};
	};
	index?: number;
	date?: string;
	providerId?: number;
}

export interface FetchTitlesOptions {
	locale: string;
	accessToken?: string;
	collectionType: CollectionType;
	filters?: Partial<FilterCollection & { min_last_change_percent: number; max_last_change_percent: number }>;
	package_intersection: boolean;
	routeQuery?: Route['query'];
	page?: number;
	pageSize?: number;
	jwId?: string;
	language?: string;
	providers?: string[];
	fields?: string[];
	paginationToken?: string;
	matchingOffersOnly?: boolean;
}

export interface FetchSimilarTitlesOptions {
	locale: string;
	language: string;
	entityIds: string[]; // in form of ts2, tm392, etc
	filters?: Partial<FilterCollection>;
	similarModelType?: string;
	titleDetailExperimentsVariants?: string;
}

const TitleHelper = {
	/* GRAPHQL BASED METHODS */

	/** Look for a monetized trailer clip if available. */
	getBackdropClips(title: TitleDetailsNode) {
		let clipsContainer: TitleDetailsNode | TitleDetailNodeSeason['show'] = title;
		if (title.objectType === ObjectType.ShowSeason && title.show != null) clipsContainer = title.show!;

		let clips = clipsContainer.content.dailymotionClips;
		if (!clips || clips.length === 0) clips = clipsContainer.content.videobusterClips;
		if (!clips || clips.length === 0) clips = clipsContainer.fallBackClips.dailymotionClips;
		if (!clips || clips.length === 0) clips = clipsContainer.fallBackClips.videobusterClips;

		// No monetized trailers at all, final fallback to YouTube
		if (!clips || clips.length === 0) clips = title.content.clips;

		return clips ?? [];
	},
	/*************************/

	timelineProviderPageSize: 5,

	async fetch(
		locale: string,
		collectionType: CollectionType,
		body = {},
		language?: string
	): Promise<PopularResponse | PopularPersonResponse> {
		const url = `${JW_CONFIG.API}/titles/${locale}/${collectionType}`;
		const params = {
			language,
			body: JSON.stringify(body),
		};
		const response = await getAxios().get(url, { params });
		return decorateFullPath(response);
	},

	async fetchProviderTitles(
		locale: string,
		collectionType: CollectionType,
		filters: Partial<FilterCollection & { min_last_change_percent: number; max_last_change_percent: number }> = {},
		page: number,
		pageSize: number
	): Promise<SingleProviderResponse> {
		if ((filters.content_types || []).indexOf(TitleObjectType.SHOW) !== -1) {
			filters.content_types = [TitleObjectType.SEASON];
		}
		const body = Object.assign({}, filters, { page, page_size: pageSize });
		if (filters.timeline_type === CollectionType.PRICEDROPS) {
			body.min_last_change_percent = -100;
			body.max_last_change_percent = -1;
		}
		const params = {
			body: JSON.stringify(body),
			filter_price_changes: filters && filters.timeline_type === CollectionType.PRICEDROPS,
		};
		const url = `${JW_CONFIG.API}/titles/${locale}/${collectionType}/single_provider`;
		return getAxios()
			.get<SingleProviderResponse>(url, { params })
			.then((response: { data: any }) => response.data);
	},

	// To-do remove
	async fetchUpcomingMovies(
		locale: string,
		year: number,
		week: number,
		params: UpcomingParams = {}
	): Promise<TitleListFull> {
		const defaultParams: UpcomingParams = {
			nationwide_cinema_releases_only: true,
		};
		const url = `${JW_CONFIG.API}/titles/movie/upcoming/${year}/${week}/locale/${locale}`;
		const concreteParams = Object.assign({}, defaultParams, params);
		const response = await getAxios().get(url, { params: concreteParams });
		return decorateFullPath(response);
	},

	fetchTitle(
		locale: string,
		objectType: TitleObjectType,
		objectId: number,
		language?: string,
		fieldsParam?: string[]
	): Promise<Title> {
		const url = `${JW_CONFIG.API}/titles/${objectType}/${objectId}/locale/${locale}`;
		let fields;
		if (fieldsParam && Array.isArray(fieldsParam)) {
			fields = fieldsParam.join(',');
		}
		const params = { language, fields };
		return getAxios()
			.get(url, { params })
			.then((response: { data: any }) => response.data);
	},

	async fetchEpisodes(
		locale: string,
		showId: number,
		params: { page_size: number; language: string; fields: string[] | string }
	): Promise<NewestEpisodesResponse> {
		const url = `${JW_CONFIG.API}/titles/show/${showId}/locale/${locale}/newest_episodes`;
		const fields = Array.isArray(params.fields) ? params.fields.join(',') : params.fields;
		return getAxios()
			.get(url, {
				params: { ...params, fields },
			})
			.then((response: { data: any }) => response.data);
	},

	async fetchTitlesWithAbort(
		{
			locale,
			collectionType,
			filters = {},
			package_intersection,
			routeQuery = {},
			page = 1,
			pageSize = 10,
			jwId,
			accessToken,
			language,
			fields = baseFields,
			paginationToken,
			matchingOffersOnly = false,
		}: FetchTitlesOptions,
		fetchOffers = true,
		cacheable = false
	): Promise<PopularResponse | NewResponse | TitleListFullResponse | WatchlistTitleResponse | any> {
		const cancelTokenSource = Axios.CancelToken.source();

		try {
			switch (collectionType) {
				case CollectionType.UPCOMING:
					fields = fields.concat('season_number', 'show_title');
				case CollectionType.POPULAR:
				case CollectionType.SEARCH:
					let monetization_types = filters.monetization_types || [];
					if (filters.timeline_type === CollectionType.POPULAR) {
						if (monetization_types.length === 0) {
							monetization_types = [
								OfferMonetizationType.ADS,
								OfferMonetizationType.BUY,
								OfferMonetizationType.FLATRATE,
								OfferMonetizationType.FREE,
								OfferMonetizationType.RENT,
								OfferMonetizationType.FLATRATE_AND_BUY,
							];
						}
					}
					const params = {
						body: JSON.stringify({
							fields:
								collectionType === CollectionType.SEARCH || fetchOffers
									? [...fields, 'offers', 'original_release_year', 'backdrops']
									: fields,
							...filters,
							package_intersection,
							...extendRuntimeFilterRange(filters),
							...{
								query: routeQuery.q,
								person_id: routeQuery.person_id ? parseInt(routeQuery.person_id as string) : undefined,
							},
							...{ monetization_types },
							...{ page, page_size: pageSize },
							matching_offers_only: matchingOffersOnly,
						}),
						language,
					};

					const url = `${JW_CONFIG.API}/titles/${locale}/popular`;

					return cancellableRequest(
						cancelTokenSource,
						(cacheable ? getAxiosCached() : getAxios())
							.get<PopularResponse>(url, { params, cancelToken: cancelTokenSource.token })
							.then((response: any) => {
								return decorateFullPath(response);
							})
					);
				case CollectionType.WATCHLIST:
				case CollectionType.SEENLIST: {
					if (!jwId) {
						return Promise.resolve([]);
					}

					const fields = [
						'id',
						'full_path',
						'full_paths',
						'genre_ids',
						'jw_entity_id',
						'episode_number',
						'last_pagination_token',
						'object_type',
						'offers',
						'original_release_year',
						'pagination_token',
						'backdrops',
						'poster',
						'scoring',
						'season_number',
						'show_title',
						'short_description',
						'title',
						'tmdb_popularity',
						'seasons.full_path',
						'seasons.poster',
						'seasons.season_number',
						'seasons.object_type',
						'seasons.id',
						'seasons.show_title',
						'episodes.episode_number',
						'episodes.object_type',
						'episodes.season_number',
						'episodes.watch_now',
						'episodes.show_id',
						'episodes.title',
						'episodes.id',
						'episodes.show_title',
					];

					let url;
					switch (filters.inner_tab) {
						case TabType.WATCH_NEXT:
							url = `${JW_CONFIG.WRITE_URL}/watch_next_v2/${locale}`;
							break;
						case TabType.COMPLETED_SEEN:
							url = `${JW_CONFIG.WRITE_URL}/caught_up_v2/${locale}`;
							break;
						case TabType.LIKE:
							url = `${JW_CONFIG.WRITE_URL}/title_list/${TitleListName.LIKELIST}/full/locale/${locale}`;
							break;
						case TabType.DISLIKE:
							url = `${JW_CONFIG.WRITE_URL}/title_list/${TitleListName.DISLIKELIST}/full/locale/${locale}`;
							break;
						default:
							// always default to TabType.WATCH_NEXT and ContentType Show
							filters.inner_tab = TabType.WATCH_NEXT;
							filters.content_types = [TitleObjectType.SHOW];
							url = `${JW_CONFIG.WRITE_URL}/watch_next_v2/${locale}`;
					}

					if (!filters.content_types) {
						filters.content_types = [TitleObjectType.SHOW];
					}

					const body = {
						fields,
						...filters,
						...{ page, page_size: pageSize, last_pagination_token: paginationToken },
						...extendRuntimeFilterRange(filters),
					};

					const params = { justwatch_id: jwId, language };
					const headers = await getAuthHeaders(accessToken);

					return cancellableRequest(
						cancelTokenSource,
						(cacheable ? getAxiosCached() : getAxios())
							.post<TitleListFullResponse>(url, body, {
								params,
								cancelToken: cancelTokenSource.token,
								headers,
							})
							.then((resp: { data: any }) => resp.data)
					);
				}
				case CollectionType.NEW:
				case CollectionType.PRICEDROPS: {
					const fields = [
						'full_path',
						'id',
						'jw_entity_id',
						'object_type',
						'offers',
						'poster',
						'scoring',
						'season_number',
						'show_id',
						'show_title',
						'title',
						'tmdb_popularity',
						'backdrops',
					];

					if ((filters.content_types || []).indexOf(TitleObjectType.SHOW) !== -1) {
						filters.content_types = [TitleObjectType.SEASON];
					}
					// only on new timeline we want to fallback fo all monetization types
					if ((filters.monetization_types || []).length === 0) {
						filters.monetization_types = OfferMonetizationTypes.map(item => item.value);
					}
					const body = Object.assign(filters, {
						page,
						page_size: pageSize,
						fields,
						...extendRuntimeFilterRange(filters),
					});
					if (filters.timeline_type === CollectionType.PRICEDROPS) {
						body.min_last_change_percent = -100;
						body.max_last_change_percent = -1;
					}

					const params = {
						body: JSON.stringify(body),
						filter_price_changes: filters.timeline_type === CollectionType.PRICEDROPS,
						language,
					};
					const url = `${JW_CONFIG.API}/titles/${locale}/new`;

					return cancellableRequest(
						cancelTokenSource,
						(cacheable ? getAxiosCached() : getAxios())
							.get<NewResponse>(url, { params })
							.then((response: { data: { days: any[] } }) => {
								return {
									...response.data,
									days: response.data.days.map((day: { providers: any[] }) => {
										// Ref #1: trick to trim total HTML size for ssr rendered new timeline
										// SSR limit total object based on global configuration (ENV vars)
										// When client takes over and scroll 1st day will be reloaded completely
										if (process.server && body.page === 1) {
											day.providers = day.providers.slice(0, NEW_TIMELINE_BUCKETS_LIMIT);
										}

										return {
											...day,
											providers: day.providers.map((provider: { items: any[] }) => {
												const items = provider.items.map((item: { offers: any }) => {
													const elements = (item.offers || [])
														.map(
															(offer: { new_element_count: any }) =>
																offer.new_element_count
														)
														.filter(Boolean)
														.concat(0);
													const newElementCount = Math.max(...(elements as number[]));
													return {
														...item,
														newElementCount,
													};
												});

												return {
													...provider,
													items,
												};
											}),
										};
									}),
								};
							})
					);
				}
				default:
					throw new Error(`Unable to fetch titles of collection type: ${collectionType}`);
			}
		} catch (error) {
			console.log('[TitleHelper] fetchTitlesWithAbort', error);
		}
	},

	getPagingOptions(
		collectionType: CollectionType,
		append: boolean,
		collectionResponse: PopularResponse | NewResponse | TitleStoreStateList,
		routeQuery = {} as Route['query'],
		filters = {} as FilterCollection
	) {
		const result: PageOptions = {
			page: 1,
			page_size: 30,
		};

		switch (collectionType) {
			case CollectionType.UPCOMING:
			case CollectionType.POPULAR: {
				// PopularResponse
				const response = collectionResponse as PopularResponse;
				result.page = append && response && response.page ? response.page + 1 : 1;
				result.page_size = response && response.page_size ? response.page_size : 30;
				break;
			}
			case CollectionType.NEW: {
				const response = collectionResponse as NewResponse;
				result.page = append && response && response.page ? response.page + 1 : 1;
				result.page_size = response && response.page_size ? response.page_size : 1;

				// special case for single provider pages on server to increase the days to 5
				if (process.server && filters?.providers?.length === 1) {
					result.page_size = 5;
				}

				break;
			}
			case CollectionType.WATCHLIST: {
				const response = collectionResponse as PopularResponse;
				result.page = append && response && response.page ? response.page + 1 : 1;
				result.page_size = 20;

				break;
			}
			case CollectionType.SEARCH: {
				const response = collectionResponse as PopularResponse;
				result.page = append && response && response.page ? response.page + 1 : 1;
				if (routeQuery.person_id) {
					result.page_size = 500;
				} else {
					result.page_size = append && response && response.page_size ? response.page_size : 20;
				}
				break;
			}
			default:
				console.log('@todo getPaginationOptions can’t handle', collectionType);
				result.page = 1;
				result.page_size = 10;
				break;
		}

		return result;
	},

	getTimelinePagingOptions(dayProvider?: NewProviderResponse) {
		const page = Math.ceil((dayProvider?.items.length ?? 0) / this.timelineProviderPageSize) + 1;

		// If for some reason the page is greater than the number of pages, then we would log a sentry error to help figure out what's going on
		if (page > Math.ceil((dayProvider?.total ?? 0) / this.timelineProviderPageSize)) {
			const errorPayload = {
				page,
				dayProvider,
				message: 'Timeline paging calculation issue',
				name: '',
			};

			captureMessageForSentry(
				'Timeline paging calculation issue:',
				{ error: errorPayload, where: '[title-helper.ts] getTimelinePagingOptions' },
				Sentry.Severity.Error
			);
		}

		return <PageOptions>{
			page,
			page_size: this.timelineProviderPageSize,
		};
	},

	/**
	 * retrieve Titles and filter them, or get specific titles with the entityId array of strings
	 *
	 * @param {string} locale
	 * @param {string[]} entityIds
	 * @param {Partial<FilterCollection>} filters
	 * @param {number} page
	 * @param {number} page_size
	 */
	async getTitles(
		locale: string,
		entityIds: string[], // in form of ts2, tm392, etc
		filters?: Partial<FilterCollection>,
		page = 1,
		page_size = 10
	): Promise<PopularResponse> {
		const url = `${JW_CONFIG.API}/titles/${locale}/popular`;
		const params = {
			body: {
				jw_entity_ids: entityIds,
				...filters,
				page_size,
				page,
			},
		};
		const response = await getAxios().get(url, { params });
		return decorateFullPath(response);
	},

	async getTrendingTitles(
		locale: string,
		language: string,
		content_types: TitleObjectType[] = [TitleObjectType.MOVIE, TitleObjectType.SHOW]
		// TODO Should have proper types: all paginated objects should use generic or union etc.
	): Promise<{
		items: PopularTitle[];
		page_size: number;
		total_pages: number;
		total_results: number;
	}> {
		const filters = {
			content_types,
			sort_by: TitleOrder.TRENDING,
		};
		const params = {
			language,
			body: JSON.stringify({
				...filters,
				...{ page_size: 10 },
			}),
		};
		const url = `${JW_CONFIG.API}/titles/${locale}/popular`;

		const response = await getAxios().get(url, { params });
		return decorateFullPath(response);
	},

	async getSimilarTitles({
		locale,
		language,
		entityIds,
		filters,
		similarModelType,
		titleDetailExperimentsVariants = '',
	}: FetchSimilarTitlesOptions): Promise<PopularResponse> {
		const url = `${JW_CONFIG.API}/titles/${locale}/recommendations`;
		const fields = baseFields.filter(field => field !== 'backdrops');
		const params = {
			language,
			jw_entity_ids: entityIds.join(','),
			body: {
				...filters,
				page_size: 12,
				page: 1,
				fields: [...fields, 'tracking_token'],
			},
			similar_model_type: similarModelType,
		};

		const now = new Date();
		const headers: AxiosRequestHeaders = titleDetailExperimentsVariants
			? {
					'Timezone-Offset': now.getTimezoneOffset().toString(),
					'Experiments-Variants': titleDetailExperimentsVariants,
			  }
			: {};

		const response = await getAxios().get(url, { params, headers });
		return decorateFullPath(response);
	},

	async getSimilarUpcomingTitles({
		locale,
		genres,
		contentType,
	}: {
		locale: string;
		genres: string[];
		contentType: TitleObjectType;
	}) {
		return await this.getTitles(locale, [], { is_upcoming: true, genres, content_types: [contentType] });
	},

	async mutateSeenList(mutations: SeenMutation[], justwatch_id: string, locale: string, accessToken: string) {
		let url = `${JW_CONFIG.WRITE_URL}/title_list/${CollectionType.SEENLIST}/mutate/${locale}`;
		const headers = await getAuthHeaders(accessToken);

		return getAxios().post(url, { mutations }, { params: { justwatch_id }, headers });
	},

	/**
	 * track a title related event with all needed contexts
	 *
	 * @param {string} evtName snowplow category
	 * @param {TitleEventPayload} payload data required
	 * @param {string} evtProperty snowplow event se_property
	 *
	 * @emits trackEvent "userinteraction"
	 */
	trackTitleEvent(evtName: string, payload: TitleEventPayload, evtProperty: string, contexts?: SnowplowContext[]) {
		const { title, index = '', date = '', provider = {} } = payload;
		const { provider_id = '' } = provider as NewProviderResponse;

		// if we have a date/provider_id in the payload we use them as label for BI, ortherwise we use title
		const label = `${date},${provider_id}`;

		const properties = {
			action: evtName,
			label: label,
			value: index,
			property: evtProperty,
		};

		// we attach filter context and title context (also Development)
		const titleContext = TrackingHelper.getTitleContext(title);
		const additionalContexts: SnowplowContext[] = (contexts || []).concat([titleContext]);
		TrackingHelper.trackEvent('userinteraction', properties, additionalContexts);
	},

	trackTitleEventGraphql(
		evtName: string,
		payload: TitleEventPayloadGraphql,
		evtProperty: string,
		contexts?: SnowplowContext[]
	) {
		const { title, index = '', date = '', providerId = '' } = payload;

		// if we have a date/provider_id in the payload we use them as label for BI, ortherwise we use title
		const label = `${date},${providerId}`;

		const properties = {
			action: evtName,
			label: label,
			value: index,
			property: evtProperty,
		};

		let { objectId, objectType } = title;

		if (title?.show) {
			objectId = title.show.objectId;
			objectType = title.show.objectType;
		}
		// we attach filter context and title context (also Development)
		const titleContext = TrackingHelper.getTitleContextGraphql(objectId, objectType, title?.content?.seasonNumber);
		const additionalContexts: SnowplowContext[] = (contexts || []).concat([titleContext]);
		TrackingHelper.trackEvent('userinteraction', properties, additionalContexts);
	},

	getWatchlistPayload({ title, source, contexts }: { title: Title; source: string; contexts?: SnowplowContext[] }) {
		const [backdrop] = title?.backdrops || [];
		return getListPayload(
			TitleListName.WATCHLIST,
			title,
			source,
			contexts,
			backdrop && backdrop.backdrop_url,
			title.poster
		);
	},

	getLikelistPayload({ title, source, contexts }: { title: Title; source: string; contexts?: SnowplowContext[] }) {
		return getListPayload(TitleListName.LIKELIST, title, source, contexts);
	},

	getDislikelistPayload({ title, source, contexts }: { title: Title; source: string; contexts?: SnowplowContext[] }) {
		return getListPayload(TitleListName.DISLIKELIST, title, source, contexts);
	},

	getTitleSlug(title: NewTitle | Title) {
		const fullPath = title.full_path?.match(/([^\/]*)$/);
		return fullPath && fullPath[0] ? fullPath[0] : 'Poster';
	},
};

function getListPayload(
	titleListName: TitleListName,
	title: Title,
	source: string,
	additionalContexts?: SnowplowContext[],
	backdrop?: string,
	poster?: string
) {
	const { title: titleName, id: objectId, object_type: objectType, pagination_token: paginationToken } = title;
	if (!(objectId && objectType && titleName)) return {};

	// assembles the payload needed for the store action
	return {
		titleListName,
		objectId,
		objectType,
		title: titleName,
		source,
		additionalContexts,
		paginationToken,
		backdrop,
		poster,
	};
}

function decorateFullPath(response: any) {
	return {
		...response.data,
		items: response.data.items?.map((item: any) => {
			return {
				...item,
				full_path: item.full_path,
			};
		}),
	};
}

export function formatRuntime(runtime: number) {
	const minutes = runtime % 60;
	const hours = (runtime - minutes) / 60;

	const result = {
		translationKey: 'WEBAPP_DURATION_MINUTES',
		minutes,
		hours,
	};

	if (runtime >= 60) {
		result.translationKey = 'WEBAPP_DURATION';
	}

	return result;
}

export const TitleMap = {
	toNewWatchlistTitle(titleResponseObject: WatchlistTitleResponse): NewWatchlistTitle {
		const {
			title,
			remaining_season_episode_count,
			seen_episode_count,
			total_episode_count,
			pagination_token,
			total_season_count,
			last_seen_episode_number,
			last_seen_season_number,
			earliest_release_time,
		} = titleResponseObject;
		return {
			...title,
			remaining_season_episode_count,
			seen_episode_count,
			total_episode_count,
			pagination_token,
			total_season_count,
			last_seen_episode_number,
			last_seen_season_number,
			earliest_release_time,
		};
	},
};

export const HideSeenDislikedTitlesHelper = {
	// In case of the user has seen or disliked all titles of a giving day, the day shouldn't be
	//shown as well
	showDayInNewPage(day: NewDayResponse) {
		const totalTitles = day.providers.reduce((totalTitles, provider) => provider.total + totalTitles, 0);
		if (totalTitles <= 0) {
			day.hideDay = true;
		}
		return day;
	},
	hideSeenDislikedTitle(title: NewTitle | PopularTitle) {
		let id = title.id;
		let objectType = title.object_type;
		// On New page, the release of a new season/episode should be tested with the show_id
		// and not the season/episode id
		if (title.object_type === TitleObjectType.SEASON || title.object_type === TitleObjectType.EPISODE) {
			// @ts-ignore
			id = title.show_id;
			objectType = TitleObjectType.SHOW;
		}
		if (
			[TitleListName.SEENLIST, TitleListName.DISLIKELIST].some(titleListName =>
				getVm().$store.getters['titleList/isInList'](titleListName, objectType, id)
			)
		) {
			title.hideTitle = true;
		}
		return title;
	},
};

export default TitleHelper;
